import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { useTranslation } from 'hooks/useTranslation';
import { ErrorUtils } from 'utils';
import { Country } from 'ts/Country';

import useAuth from 'features/auth/hooks/useAuth';
import { CameFromType, CompanySizeType, RegistrationInput } from 'features/auth/ts';
import { registerUser } from 'features/auth/api';
import { AuthPageLayout } from 'features/auth/components/AuthPageLayout';
import { SignUpForm } from 'features/auth/components/SignUpForm';
import { CameFromForm } from 'features/auth/components/CameFromForm';
import { addErrorMessage, addSuccessMessage } from 'features/message/redux/action';
import { registerUserFailed, registerUserSuccess } from 'features/auth/redux/actions';
import { useLanguage } from 'hooks/useLanguage';

import { useStyles } from './styles';
import i18n from 'locales/i18n';
import { useHistory } from 'react-router-dom';
import { authPaths } from 'features/auth/routes/paths';

const INITIAL_FORM_STATE = {
  email: '',
  first_name: '',
  last_name: '',
  phone_number: '',
  company_name: '',
  country_of_business: '',
  company_size: '',
  came_from: {
    [CameFromType.A_FRIEND_OR_COLLEAGUE]: false,
    [CameFromType.SOCIAL_MEDIA]: false,
    [CameFromType.SEARCH_ENGINE]: false,
    [CameFromType.OTHER]: false,
  },
  is_user_agree_with_terms_and_conditions: false,
  language: '',
};

type SignUpStateKeys = keyof typeof INITIAL_FORM_STATE;

const FieldsToValidate = [
  'email',
  'first_name',
  'last_name',
  'phone_number',
  'company_name',
  'country_of_business',
  'company_size',
  'is_user_agree_with_terms_and_conditions',
];

const mapCameFromOptionsToArray = (cameFromOptions: Record<CameFromType, boolean>): CameFromType[] => {
  const cameFromKeys = Object.keys(cameFromOptions);
  if (!cameFromKeys) return [];

  return cameFromKeys.filter(key => cameFromOptions[key as CameFromType]) as CameFromType[];
};

export const SignUp = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation('auth');
  const { error } = useAuth();

  const [isFirstPartOfSignUpShowed, setIsFirstPartOfSignUpShowed] = useState(true);
  const direction = i18n.dir();
  const { currentLanguageCode } = useLanguage();
  const history = useHistory();

  const classes = useStyles({ isFirstPartOfSignUpShowed, direction });

  const form = useForm({
    mode: 'onSubmit',
    defaultValues: INITIAL_FORM_STATE,
  });

  const pageTitle = useMemo(() => {
    if (isFirstPartOfSignUpShowed) return t('start14DaysFree');
    else return `${t('whereDidYouFirstHearAboutUs')}`;
  }, [t, isFirstPartOfSignUpShowed]);

  const isFormValid = useCallback(async () => {
    return await form.trigger(FieldsToValidate as unknown as SignUpStateKeys);
  }, [form]);

  const handleSignUp = useCallback(async () => {
    const { errors: fieldErrors } = form.formState;
    const isSignUpDataValid = await isFormValid();

    if (!isSignUpDataValid || Object.keys(fieldErrors).length) {
      form.trigger();
      return;
    }

    try {
      const formData = form.getValues();

      const { data } = await registerUser({
        ...formData,
        company_size: (formData.company_size as unknown as { code: CompanySizeType; name: string })?.code,
        country_of_business: (formData.country_of_business as unknown as Country)?.code,
        came_from: mapCameFromOptionsToArray(formData.came_from),
        language: currentLanguageCode,
      });

      dispatch(addSuccessMessage(data?.detail || t('messages.successRegistration', { ns: 'auth' })));
      dispatch(registerUserSuccess());
      form.reset();
      history.push({ pathname: authPaths.static.successfulSignUp, search: `?lng=${currentLanguageCode}` });
    } catch (error) {
      const errMsg = ErrorUtils.convertError(error);
      if (typeof errMsg === 'object') {
        dispatch(registerUserFailed(errMsg));
        dispatch(addErrorMessage(t('signUpFailed', { ns: 'app' })));
      } else {
        dispatch(addErrorMessage(errMsg));
      }
      setIsFirstPartOfSignUpShowed(true);
    }
  }, [dispatch, form, t, currentLanguageCode, isFormValid, history]);

  useEffect(() => {
    if (!error || typeof error !== 'object') return;

    const validationError = error as Record<string, unknown[]>;
    const errorKeys = Object.keys(validationError) as Array<keyof RegistrationInput>;
    errorKeys.forEach((errorKey: keyof RegistrationInput) => {
      form.setError(errorKey, { message: validationError[errorKey]?.join('\n') });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const onBaseSignUpFormSubmit = useCallback(async () => {
    const isValid = await isFormValid();
    if (!isValid) return;

    setIsFirstPartOfSignUpShowed(false);
  }, [isFormValid]);

  const onRedirectToFirstStep = useCallback(() => {
    setIsFirstPartOfSignUpShowed(true);
  }, [setIsFirstPartOfSignUpShowed]);

  return (
    <AuthPageLayout title={pageTitle} additionalContentClassName={classes.signUpFormWrapper}>
      <FormProvider {...form}>
        {isFirstPartOfSignUpShowed ? (
          <SignUpForm onSubmit={onBaseSignUpFormSubmit} />
        ) : (
          <CameFromForm redirectToFirstStep={onRedirectToFirstStep} onSignUpFormSubmit={handleSignUp} />
        )}
      </FormProvider>
    </AuthPageLayout>
  );
};
